import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  FiShield, FiCamera, FiUsers, FiHeadphones, FiTool, FiSearch,
  FiCheckCircle, FiAward, FiZap, FiBarChart2, FiArrowRight, FiPhone,
  FiEye, FiLock, FiAlertTriangle, FiX, FiChevronLeft, FiChevronRight,
  FiMaximize2, FiImage,
} from 'react-icons/fi';
import { BsShieldFillCheck, BsPersonBadge } from 'react-icons/bs';
import { MdOutlineSecurity, MdOutlineVerifiedUser } from 'react-icons/md';

const ALL_IMAGES = [
  'WhatsApp Image 2026-03-27 at 17.20.06.jpeg',
  'WhatsApp Image 2026-03-27 at 17.20.06 (1).jpeg',
  'WhatsApp Image 2026-03-27 at 17.20.07.jpeg',
  'WhatsApp Image 2026-03-27 at 17.20.07 (1).jpeg',
  'WhatsApp Image 2026-03-27 at 17.20.07 (2).jpeg',
  'WhatsApp Image 2026-03-27 at 17.20.08.jpeg',
  'WhatsApp Image 2026-03-27 at 17.20.08 (1).jpeg',
  'WhatsApp Image 2026-03-27 at 17.20.08 (2).jpeg',
  'WhatsApp Image 2026-03-27 at 17.20.10.jpeg',
  'WhatsApp Image 2026-03-27 at 17.20.11.jpeg',
  'WhatsApp Image 2026-03-27 at 17.20.11 (1).jpeg',
  'WhatsApp Image 2026-03-27 at 17.20.12.jpeg',
  'WhatsApp Image 2026-03-27 at 17.20.12 (1).jpeg',
  'WhatsApp Image 2026-03-27 at 17.20.13.jpeg',
  'WhatsApp Image 2026-03-27 at 17.20.13 (1).jpeg',
  'WhatsApp Image 2026-03-27 at 17.20.14.jpeg',
  'WhatsApp Image 2026-03-27 at 17.20.14 (1).jpeg',
  'WhatsApp Image 2026-03-27 at 17.20.14 (2).jpeg',
  'WhatsApp Image 2026-03-27 at 17.20.15.jpeg',
];

const stats = [
  { value: '10+',   label: 'Years Experience' },
  { value: '500+',  label: 'Clients Served' },
  { value: '1,000+', label: 'Personnel Deployed' },
  { value: '2',     label: 'Regional Offices' },
];

const services = [
  { Icon: FiShield,     title: 'Security Services',         desc: 'Corporate, residential, event, escort, and personal bodyguard security with 24/7 coverage.' },
  { Icon: FiCamera,     title: 'Digital Security',          desc: 'CCTV installation, electric fence systems, and gate motor automation with full support.' },
  { Icon: FiUsers,      title: 'Labour Supply',             desc: 'Skilled & unskilled manpower, technicians, drivers, and trade specialists on demand.' },
  { Icon: FiHeadphones, title: 'Customer Service Agents',   desc: 'Professional front desk, event ushering, and call-centre support agents.' },
  { Icon: FiTool,       title: 'Casual Labour',             desc: 'Verified workers for events, loading, cleaning, maintenance, and minor construction.' },
  { Icon: FiSearch,     title: 'Recruitment Agency',        desc: 'End-to-end placement of security officers, technicians, and administrative staff.' },
];

const whyUs = [
  { Icon: FiCheckCircle, title: 'Background-Verified',  desc: 'All personnel undergo thorough vetting and induction before deployment.' },
  { Icon: FiAward,       title: 'Licensed & Compliant', desc: 'Fully registered and operating under Tanzanian regulations.' },
  { Icon: FiZap,         title: 'Rapid Response',       desc: 'Fast mobilisation for emergency and short-notice requirements.' },
  { Icon: FiBarChart2,   title: 'Quality Assured',      desc: 'Documented KPIs, regular audits, and supervision at every site.' },
];

export default function Home() {
  const INITIAL_COUNT = 8;
  const [showAll, setShowAll]       = useState(false);
  const [lightbox, setLightbox]     = useState(null); // index or null

  const visibleImages = showAll ? ALL_IMAGES : ALL_IMAGES.slice(0, INITIAL_COUNT);

  const openLightbox  = (i) => setLightbox(i);
  const closeLightbox = () => setLightbox(null);
  const prev = useCallback(() => setLightbox(i => (i - 1 + ALL_IMAGES.length) % ALL_IMAGES.length), []);
  const next = useCallback(() => setLightbox(i => (i + 1) % ALL_IMAGES.length), []);

  useEffect(() => {
    if (lightbox === null) return;
    document.body.style.overflow = 'hidden';
    const handler = (e) => {
      if (e.key === 'ArrowLeft')  prev();
      if (e.key === 'ArrowRight') next();
      if (e.key === 'Escape')     closeLightbox();
    };
    window.addEventListener('keydown', handler);
    return () => {
      document.body.style.overflow = '';
      window.removeEventListener('keydown', handler);
    };
  }, [lightbox, prev, next]);

  return (
    <div className="animate-fadeUp">

      {/* ── LIGHTBOX ── */}
      {lightbox !== null && (
        <div className="fixed inset-0 z-[100] flex flex-col bg-black/96" onClick={closeLightbox}>

          {/* ── Top bar ── */}
          <div
            className="flex-shrink-0 flex items-center justify-between px-5 py-3 bg-black/40 border-b border-white/5"
            onClick={e => e.stopPropagation()}
          >
            <div className="flex items-center gap-2">
              <img src="/logo/logo.jpeg" alt="KIKI Security" className="h-8 object-contain" />
            </div>
            <div className="text-gray-400 text-sm">
              <span className="text-white font-bold">{lightbox + 1}</span> / {ALL_IMAGES.length}
            </div>
            <button
              onClick={closeLightbox}
              className="w-9 h-9 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white transition-colors"
            >
              <FiX size={18} />
            </button>
          </div>

          {/* ── Main image area ── */}
          <div className="flex-1 flex items-center justify-center relative min-h-0 px-16 py-4">
            {/* Prev */}
            <button
              onClick={e => { e.stopPropagation(); prev(); }}
              className="absolute left-3 w-12 h-12 rounded-full bg-white/10 hover:bg-gold hover:text-navy flex items-center justify-center text-white transition-all duration-200 z-10"
            >
              <FiChevronLeft size={24} />
            </button>

            {/* Image */}
            <img
              key={lightbox}
              src={`/images/${encodeURIComponent(ALL_IMAGES[lightbox])}`}
              alt={`KIKI Security ${lightbox + 1}`}
              className="max-h-full max-w-full object-contain rounded-xl shadow-[0_8px_60px_rgba(0,0,0,0.8)] animate-fadeUp"
              onClick={e => e.stopPropagation()}
            />

            {/* Next */}
            <button
              onClick={e => { e.stopPropagation(); next(); }}
              className="absolute right-3 w-12 h-12 rounded-full bg-white/10 hover:bg-gold hover:text-navy flex items-center justify-center text-white transition-all duration-200 z-10"
            >
              <FiChevronRight size={24} />
            </button>
          </div>

          {/* ── Thumbnail strip ── */}
          <div
            className="flex-shrink-0 bg-black/40 border-t border-white/5 px-4 py-3"
            onClick={e => e.stopPropagation()}
          >
            <div className="flex gap-2 overflow-x-auto pb-1 justify-center">
              {ALL_IMAGES.map((file, i) => (
                <button
                  key={i}
                  onClick={() => setLightbox(i)}
                  className={`flex-shrink-0 w-14 h-14 rounded-lg overflow-hidden transition-all duration-200 ${
                    i === lightbox
                      ? 'ring-2 ring-gold opacity-100 scale-105'
                      : 'opacity-35 hover:opacity-70'
                  }`}
                >
                  <img
                    src={`/images/${encodeURIComponent(file)}`}
                    alt=""
                    className="w-full h-full object-cover"
                  />
                </button>
              ))}
            </div>
          </div>

        </div>
      )}

      {/* ── HERO ── */}
      <section className="relative min-h-screen flex items-center overflow-hidden">
        {/* Background image */}
        <div
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{ backgroundImage: "url('https://images.unsplash.com/photo-1582139329536-e7284fece509?w=1800&q=80')" }}
        />
        {/* 50% navy overlay */}
        <div className="absolute inset-0 bg-navy-DEFAULT/50" />
        {/* gold radial glow */}
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,rgba(201,168,76,0.14),transparent_60%)]" />
        {/* subtle grid */}
        <div
          className="absolute inset-0 opacity-[0.04]"
          style={{ backgroundImage: 'linear-gradient(rgba(255,255,255,1) 1px,transparent 1px),linear-gradient(90deg,rgba(255,255,255,1) 1px,transparent 1px)', backgroundSize: '60px 60px' }}
        />

        <div className="relative max-w-7xl mx-auto px-6 pt-32 pb-20 w-full">
          <div className="flex flex-col lg:flex-row gap-12 items-center">

            {/* ── Left: text ── */}
            <div className="flex-1">
              <span className="section-tag">Est. 2015 — Tanzania</span>
              <h1 className="font-display text-5xl md:text-6xl lg:text-7xl font-extrabold leading-[1.1] mt-2 mb-6">
                Protecting What <br />
                <span className="text-gold">Matters Most</span>
              </h1>
              <p className="text-white text-lg md:text-xl leading-relaxed max-w-xl mb-10 drop-shadow-[0_2px_8px_rgba(0,0,0,0.8)]">
                KIKI SECURITY (T) LTD delivers integrated security guarding, cutting-edge technology systems,
                and agile workforce solutions across Tanzania — on time and on budget.
              </p>
              <div className="flex flex-wrap gap-4">
                <Link to="/contact" className="btn-primary text-base">
                  Get a Free Quote <FiArrowRight size={16} />
                </Link>
                <Link to="/services" className="btn-outline text-base">
                  Our Services
                </Link>
              </div>

              {/* trust badges */}
              <div className="flex flex-wrap gap-5 mt-10">
                {[
                  [FiCheckCircle, '24/7 Coverage'],
                  [MdOutlineVerifiedUser, 'Licensed & Certified'],
                  [FiShield, 'Background Verified'],
                ].map(([Icon, label], i) => (
                  <div key={i} className="flex items-center gap-2 text-white text-sm drop-shadow-[0_1px_4px_rgba(0,0,0,0.8)]">
                    <Icon className="text-gold" size={15} />
                    {label}
                  </div>
                ))}
              </div>
            </div>

            {/* ── Right: security visual ── */}
            <div className="hidden lg:flex justify-center items-center">
              <div className="relative w-[420px] h-[420px]">

                {/* outer ring */}
                <div className="absolute inset-0 rounded-full border border-gold/20 animate-[spin_30s_linear_infinite]" />
                <div className="absolute inset-6 rounded-full border border-gold/10 animate-[spin_20s_linear_infinite_reverse]" />

                {/* glow backdrop */}
                <div className="absolute inset-0 rounded-full bg-[radial-gradient(ellipse_at_center,rgba(201,168,76,0.08),transparent_70%)]" />

                {/* centre shield */}
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="relative flex items-center justify-center">
                    {/* outer shield ring */}
                    <div className="absolute w-56 h-56 rounded-full bg-navy-light/60 border border-gold/20 shadow-[0_0_60px_rgba(201,168,76,0.15)]" />
                    {/* inner circle */}
                    <div className="absolute w-40 h-40 rounded-full bg-navy-card border border-gold/30 shadow-[0_0_40px_rgba(201,168,76,0.2)]" />
                    {/* main icon */}
                    <BsShieldFillCheck className="text-gold relative z-10 drop-shadow-[0_0_20px_rgba(201,168,76,0.8)]" size={80} />
                  </div>
                </div>

                {/* orbit icons */}
                {[
                  { Icon: BsPersonBadge,         label: 'Guard',      angle: 0   },
                  { Icon: FiCamera,              label: 'CCTV',       angle: 72  },
                  { Icon: MdOutlineSecurity,     label: 'Armed',      angle: 144 },
                  { Icon: FiLock,                label: 'Access',     angle: 216 },
                  { Icon: FiAlertTriangle,       label: 'Response',   angle: 288 },
                ].map(({ Icon, label, angle }) => {
                  const rad = (angle - 90) * (Math.PI / 180);
                  const r = 170;
                  const x = 50 + (r / 4.2) * Math.cos(rad);
                  const y = 50 + (r / 4.2) * Math.sin(rad);
                  return (
                    <div
                      key={label}
                      className="absolute flex flex-col items-center gap-1"
                      style={{ left: `${x}%`, top: `${y}%`, transform: 'translate(-50%,-50%)' }}
                    >
                      <div className="w-12 h-12 rounded-xl bg-navy-card border border-gold/25 flex items-center justify-center shadow-[0_4px_20px_rgba(0,0,0,0.4)] hover:border-gold/60 transition-colors">
                        <Icon className="text-gold" size={20} />
                      </div>
                      <span className="text-gray-400 text-[10px] font-medium tracking-wide">{label}</span>
                    </div>
                  );
                })}

                {/* eye icon top-right decorative */}
                <div className="absolute top-4 right-8 flex items-center gap-1.5 bg-navy-card/80 border border-gold/20 rounded-full px-3 py-1.5">
                  <FiEye className="text-gold" size={12} />
                  <span className="text-gray-400 text-[10px] font-medium">24 / 7 Active</span>
                </div>

                {/* dot decorations */}
                {[...Array(8)].map((_, i) => {
                  const a = (i * 45 - 90) * (Math.PI / 180);
                  const x2 = 50 + 47 * Math.cos(a);
                  const y2 = 50 + 47 * Math.sin(a);
                  return (
                    <div
                      key={i}
                      className="absolute w-1.5 h-1.5 rounded-full bg-gold/40"
                      style={{ left: `${x2}%`, top: `${y2}%`, transform: 'translate(-50%,-50%)' }}
                    />
                  );
                })}
              </div>
            </div>

          </div>
        </div>

        <div className="absolute bottom-0 inset-x-0 h-32 bg-gradient-to-t from-navy-DEFAULT to-transparent" />
      </section>

      {/* ── STATS ── */}
      <section className="py-12 bg-navy-card">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map(s => (
              <div key={s.label} className="text-center">
                <div className="font-display text-4xl font-extrabold text-gold mb-1">{s.value}</div>
                <div className="text-gray-400 text-sm font-medium">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── SERVICES OVERVIEW ── */}
      <section className="py-24 max-w-7xl mx-auto px-6">
        <div className="text-center mb-14">
          <span className="section-tag">What We Offer</span>
          <h2 className="section-title">
            Comprehensive <span className="text-gold">Security & Workforce</span> Solutions
          </h2>
          <div className="gold-divider mx-auto" />
          <p className="text-gray-400 max-w-xl mx-auto mt-2">
            From physical guarding to digital systems and skilled manpower — one trusted partner for all your needs.
          </p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {services.map(({ Icon, title, desc }) => (
            <div key={title} className="card group">
              <div className="w-12 h-12 rounded-lg bg-gold/10 flex items-center justify-center mb-4 group-hover:bg-gold/20 transition-colors">
                <Icon className="text-gold" size={22} />
              </div>
              <h3 className="text-white font-semibold text-lg mb-2">{title}</h3>
              <p className="text-gray-400 text-sm leading-relaxed">{desc}</p>
            </div>
          ))}
        </div>
        <div className="text-center mt-10">
          <Link to="/services" className="btn-outline">
            View All Services <FiArrowRight size={15} />
          </Link>
        </div>
      </section>

      {/* ── WHY CHOOSE US ── */}
      <section className="py-24 bg-navy-mid">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <span className="section-tag">Why KIKI Security</span>
              <h2 className="section-title">
                Built on <span className="text-gold">Trust, Reliability</span> &amp; Expertise
              </h2>
              <div className="gold-divider" />
              <p className="text-gray-400 leading-relaxed mb-8">
                Since 2015, we have built a reputation for disciplined service delivery, combining professional
                personnel with modern technology to protect lives, assets, and business continuity.
              </p>
              <Link to="/about" className="btn-primary">
                Learn About Us <FiArrowRight size={16} />
              </Link>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              {whyUs.map(({ Icon, title, desc }) => (
                <div key={title} className="bg-navy-card rounded-xl p-5 transition-all">
                  <Icon className="text-gold mb-3" size={24} />
                  <h4 className="text-white font-semibold mb-1">{title}</h4>
                  <p className="text-gray-400 text-sm leading-relaxed">{desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── GALLERY ── */}
      <section className="py-24 bg-navy-mid">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-14">
            <span className="section-tag">In the Field</span>
            <h2 className="section-title">Our Team <span className="text-gold">At Work</span></h2>
            <div className="gold-divider mx-auto" />
            <p className="text-gray-400 max-w-xl mx-auto mt-2">
              A glimpse into our daily operations — disciplined professionals protecting clients across Tanzania.
              Click any photo to view full size.
            </p>
          </div>

          {/* Grid — 8 shown by default */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
            {visibleImages.map((file, i) => (
              <button
                key={i}
                onClick={() => openLightbox(ALL_IMAGES.indexOf(file))}
                className={`group relative overflow-hidden rounded-xl bg-navy-card transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-gold/60 ${
                  i === 0 ? 'col-span-2 row-span-2' : ''
                }`}
              >
                <img
                  src={`/images/${encodeURIComponent(file)}`}
                  alt={`KIKI Security operations ${i + 1}`}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  style={{ minHeight: i === 0 ? '360px' : '200px' }}
                />
                {/* hover overlay */}
                <div className="absolute inset-0 bg-navy-DEFAULT/0 group-hover:bg-navy-DEFAULT/50 transition-all duration-300 flex flex-col items-center justify-center gap-2">
                  <FiMaximize2 className="text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300 drop-shadow-lg" size={26} />
                  <span className="text-white text-xs font-medium opacity-0 group-hover:opacity-100 transition-opacity duration-300">View Full Size</span>
                </div>
              </button>
            ))}

            {/* "Show more" card — only when collapsed */}
            {!showAll && (
              <button
                onClick={() => setShowAll(true)}
                className="group relative overflow-hidden rounded-xl bg-navy-card transition-all duration-300 flex flex-col items-center justify-center gap-3 cursor-pointer"
                style={{ minHeight: '200px' }}
              >
                <div className="w-14 h-14 rounded-full bg-gold/10 group-hover:bg-gold/20 flex items-center justify-center transition-colors">
                  <FiImage className="text-gold" size={24} />
                </div>
                <div className="text-center">
                  <div className="text-white font-semibold text-sm">+{ALL_IMAGES.length - INITIAL_COUNT} More</div>
                  <div className="text-gray-400 text-xs mt-0.5">Click to view all photos</div>
                </div>
              </button>
            )}
          </div>

          {/* Show less button */}
          {showAll && (
            <div className="text-center mt-8">
              <button onClick={() => setShowAll(false)} className="btn-outline text-sm">
                Show Less
              </button>
            </div>
          )}
        </div>
      </section>

      {/* ── CLIENTS MARQUEE ── */}
      <section className="py-20 bg-navy-DEFAULT overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 mb-12 text-center">
          <span className="section-tag">Our Clients</span>
          <h2 className="section-title mt-2">Trusted by <span className="text-gold">Leading Organisations</span></h2>
          <div className="gold-divider mx-auto" />
          <p className="text-gray-400 mt-2 text-sm">Delivering excellence to clients across Tanzania and beyond.</p>
        </div>

        {/* Marquee track */}
        <div className="relative">
          {/* left fade */}
          <div className="absolute left-0 top-0 bottom-0 w-24 z-10 bg-gradient-to-r from-navy-DEFAULT to-transparent pointer-events-none" />
          {/* right fade */}
          <div className="absolute right-0 top-0 bottom-0 w-24 z-10 bg-gradient-to-l from-navy-DEFAULT to-transparent pointer-events-none" />

          <div className="flex gap-8 w-max animate-[marquee_22s_linear_infinite] hover:[animation-play-state:paused]">
            {/* render the list twice for seamless loop */}
            {[...Array(2)].flatMap(() => [
              { src: '/clients/images-2.png',                                    name: 'Client' },
              { src: '/clients/images-3.png',                                    name: 'Client' },
              { src: '/clients/1647421533606.jpeg',                              name: 'Client' },
              { src: '/clients/png.png',                                         name: 'Client' },
              { src: '/clients/Vocation Education & Training authority ( VETA).png', name: 'VETA' },
              { src: '/clients/tazara logo.png',                                 name: 'TAZARA' },
              { src: '/clients/Honda_Logo.svg',                                  name: 'Honda' },
            ]).map((client, i) => (
              <div
                key={i}
                className="flex-shrink-0 w-44 h-24 bg-navy-card rounded-xl px-6 flex items-center justify-center transition-all duration-300 group"
              >
                <img
                  src={client.src}
                  alt={client.name}
                  className="max-h-12 max-w-full object-contain filter grayscale opacity-60 group-hover:grayscale-0 group-hover:opacity-100 transition-all duration-300"
                />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA BANNER ── */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="relative rounded-2xl overflow-hidden bg-gradient-to-r from-navy-light to-navy-mid p-10 md:p-16 text-center">
            <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(201,168,76,0.08),transparent_70%)]" />
            <div className="relative">
              <h2 className="section-title mb-4">
                Ready to Secure Your <span className="text-gold">Business?</span>
              </h2>
              <p className="text-gray-400 max-w-lg mx-auto mb-8">
                Contact us today for a free site assessment and detailed quotation tailored to your needs.
              </p>
              <div className="flex flex-wrap justify-center gap-4">
                <Link to="/contact" className="btn-primary">
                  Get a Free Quote <FiArrowRight size={16} />
                </Link>
                <a href="tel:+255552222117" className="btn-outline">
                  <FiPhone size={15} /> Call Us Now
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

    </div>
  );
}
